import {
  ALL_ALCHEMY_TYPES, ALL_BODY_GENES, ALL_EAR_GENES, ALL_HORN_GENES,
  ALL_MARKING_GENES, ALL_TAIL_GENES,
  type AlchemyType, type BodyGene, type EarGene, type HornGene,
  type MarkingGene, type TailGene,
} from '@/domain/genetics/types';
import type { BiomeId } from '@/domain/world/types';

export interface WeightedValue<T> { value: T; weight: number }

export interface BiomeEcologyProfile {
  types: ReadonlyArray<WeightedValue<AlchemyType>>;
  bodies: ReadonlyArray<WeightedValue<BodyGene>>;
  ears: ReadonlyArray<WeightedValue<EarGene>>;
  horns: ReadonlyArray<WeightedValue<HornGene>>;
  tails: ReadonlyArray<WeightedValue<TailGene>>;
  markings: ReadonlyArray<WeightedValue<MarkingGene>>;
  hueRanges: ReadonlyArray<{ min: number; max: number; weight: number }>;
}

function anatomyWeights<T>(values: readonly T[], favored: readonly T[], contrary: readonly T[] = []): WeightedValue<T>[] {
  return values.map((value) => ({
    value,
    weight: favored.includes(value) ? 5 : contrary.includes(value) ? 1 : 2,
  }));
}

function typeWeights(first: AlchemyType, favored: readonly AlchemyType[]): WeightedValue<AlchemyType>[] {
  return ALL_ALCHEMY_TYPES.map((value) => ({
    value,
    weight: value === first ? 6 : favored.includes(value) ? 4 : 1,
  }));
}

function profile(input: {
  firstType: AlchemyType; types: readonly AlchemyType[];
  bodies: readonly BodyGene[]; contraryBodies?: readonly BodyGene[];
  ears: readonly EarGene[]; contraryEars?: readonly EarGene[];
  horns: readonly HornGene[]; contraryHorns?: readonly HornGene[];
  tails: readonly TailGene[]; contraryTails?: readonly TailGene[];
  markings: readonly MarkingGene[]; contraryMarkings?: readonly MarkingGene[];
  hue: readonly [number, number];
}): BiomeEcologyProfile {
  return Object.freeze({
    types: Object.freeze(typeWeights(input.firstType, input.types)),
    bodies: Object.freeze(anatomyWeights(ALL_BODY_GENES, input.bodies, input.contraryBodies)),
    ears: Object.freeze(anatomyWeights(ALL_EAR_GENES, input.ears, input.contraryEars)),
    horns: Object.freeze(anatomyWeights(ALL_HORN_GENES, input.horns, input.contraryHorns)),
    tails: Object.freeze(anatomyWeights(ALL_TAIL_GENES, input.tails, input.contraryTails)),
    markings: Object.freeze(anatomyWeights(ALL_MARKING_GENES, input.markings, input.contraryMarkings)),
    hueRanges: Object.freeze([
      Object.freeze({ min: input.hue[0], max: input.hue[1], weight: 5 }),
      Object.freeze({ min: 0, max: 359, weight: 1 }),
    ]),
  });
}

export const BIOME_ECOLOGY: Readonly<Record<BiomeId, BiomeEcologyProfile>> = Object.freeze({
  meadow: profile({
    firstType: 'Life', types: ['Life', 'Air', 'Earth'], bodies: ['round', 'blob'], contraryBodies: ['fish'],
    ears: ['floppy', 'round'], horns: ['nub'], contraryHorns: ['crown'], tails: ['curl', 'stub'], contraryTails: ['fin'],
    markings: ['cheeks', 'freckles'], hue: [35, 145],
  }),
  forest: profile({
    firstType: 'Life', types: ['Life', 'Earth', 'Shadow'], bodies: ['tall', 'round'], contraryBodies: ['fish'],
    ears: ['pointy'], horns: ['crown', 'nub'], tails: ['curl'], contraryTails: ['fin'],
    markings: ['stripes', 'mask'], hue: [70, 170],
  }),
  cave: profile({
    firstType: 'Shadow', types: ['Shadow', 'Earth', 'Fire'], bodies: ['ghost', 'blob'], contraryBodies: ['fish'],
    ears: ['antenna', 'pointy'], horns: ['twin', 'crown'], tails: ['none', 'stub'], contraryTails: ['fin'],
    markings: ['mask', 'stripes'], hue: [190, 310],
  }),
  coast: profile({
    firstType: 'Water', types: ['Water', 'Air', 'Life'], bodies: ['fish', 'blob'], contraryBodies: ['ghost'],
    ears: ['round', 'floppy'], horns: ['none', 'nub'], contraryHorns: ['crown'], tails: ['fin'],
    markings: ['cheeks', 'freckles'], hue: [170, 225],
  }),
});

export function ecologyForBiome(biome: BiomeId): BiomeEcologyProfile {
  return BIOME_ECOLOGY[biome];
}
